// token-refresh.interceptor.ts
import { HttpErrorResponse, HttpHeaders, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, finalize, map, shareReplay, switchMap, throwError } from 'rxjs';
import { AuthService } from '@services/auth.service';
import { BaseErrorHandler } from '@core/interceptors/base.error-handler';
import { SessionModel } from '@shared/models/session.model';
import { TokenInterceptorMessages } from '@core/interceptors/enum/token.interceptor.enum';

let refresh$: ReturnType<AuthService['sessionRefresh']> | null = null;

export const tokenRefreshInterceptor: HttpInterceptorFn = (req, next) => {
    const auth = inject(AuthService);
    const base = inject(BaseErrorHandler);

    return next(req).pipe(
        catchError((error: HttpErrorResponse) => {
            switch (error.status) {
                case 400:
                    return handleBadRequest(auth, error);
                case 401:
                    return handleUnauthorized(auth, base, req, next, error);
                case 403:
                    return handleForbidden(error);
                default:
                    return throwError(() => error);
            }
        })
    );
};

function handleBadRequest(auth: AuthService, error: HttpErrorResponse) {
    const cases = [TokenInterceptorMessages.TokenMissing, TokenInterceptorMessages.TokenFormatWrong];
    if (cases.includes(error.error)) {
        return auth.handleRemoveSession(error); // deine bestehende Logik
    }
    return throwError(() => error);
}

function handleUnauthorized(auth: AuthService, base: BaseErrorHandler, req: HttpRequest<any>, next: Parameters<HttpInterceptorFn>[1], error: HttpErrorResponse) {
    const reason = error.error;

    if (reason === TokenInterceptorMessages.SessionExpired) {
        if (!refresh$) {
            refresh$ = auth.sessionRefresh().pipe(
                finalize(() => {
                    refresh$ = null;
                }),
                shareReplay(1)
            );
        }

        return refresh$.pipe(
            map((s: SessionModel) => s.token),
            switchMap((token) => next(addToken(req, token))),
            catchError((error) => {
                auth.removeSession();
                return throwError(() => error);
            })
        );
    }

    if (reason === TokenInterceptorMessages.NoSessionForToken) {
        return auth.handleRemoveSession(error);
    }

    return throwError(() => error);
}

function handleForbidden(error: HttpErrorResponse) {
    return throwError(() => error);
}

function addToken(request: HttpRequest<any>, token?: string) {
    if (!token) return request;
    const headers: HttpHeaders = (request.headers ?? new HttpHeaders()).set('x-session-token', token);
    return request.clone({ headers });
}
